import { useId } from 'react';
import Icons from '../Icons';
import { InputWrapper } from '../Input';
import { Spinner } from '../Spinner';
import { type MultiProps, MultiSelect } from './MultiSelect';
import { type SingleProps, SingleSelect } from './SingleSelect';

type CommonSelectProps = {
  id?: string;
  name?: string;
  label?: string;
  disabled?: boolean;
  skipMargin?: boolean;
  error?: string | null;
  helperText?: string;
  loading?: boolean;
  'data-testid'?: string;
};

type SelectProps<T> = CommonSelectProps & (MultiProps<T> | SingleProps<T>);

export const Select = <T,>(props: SelectProps<T>) => {
  const labelId = useId();

  const hasContent = props.isMulti
    ? props.value.length > 0
    : props.options.some(option =>
        'options' in option
          ? option.options.some(o => o.value === props.value)
          : option.value === props.value,
      );
  const disabled = props.disabled || props.loading;

  return (
    <InputWrapper
      disabled={disabled}
      label={props.label}
      skipMargin={props.skipMargin}
      hasContent={hasContent}
      helperText={props.helperText}
      error={props.error}
      trailingIcon={props.loading ? <Spinner size="xs" /> : <Icons.ChevronDown />}
      labelId={labelId}
    >
      {props.isMulti ? (
        <MultiSelect
          {...(props as MultiProps<T>)}
          disabled={disabled}
          hasContent={hasContent}
          labelId={labelId}
        />
      ) : (
        <SingleSelect
          {...(props as SingleProps<T>)}
          disabled={disabled}
          hasContent={hasContent}
          labelId={labelId}
        />
      )}
    </InputWrapper>
  );
};
